import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import cn from 'classnames';
import {
  useForm,
  Controller,
  useFieldArray,
  FormProvider,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Button,
  Divider,
  Input,
  Select,
  SubmitActions,
  TitleSecondary,
} from '../../components';

import GoalsGroupForm from './goals.group.form';

import { config } from '../../api/config';
import { actions } from '../../state';

import {
  dateFormat,
  loyaltyProgramFormDefaultValues,
  loyaltyProgramDefaultValuesMapper,
  generateGoalValues,
} from './loyaltyProgram.data';
import { loyaltyProgramSchema } from './loyaltyProgram.validation';

import styles from './loyaltyProgram.module.scss';

const periodOptions = [
  { name: 'Mėnuo', value: 63 },
  { name: 'Galiojimo laikas', value: 62 },
];

const Form = ({
  dispatch,
  defaultValues,
  onSubmit,
  isEdit,
  loyaltyProgram: { loading },
  memberships,
}) => {
  const mappedDefaults =
    defaultValues && isEdit
      ? loyaltyProgramDefaultValuesMapper(defaultValues)
      : defaultValues || loyaltyProgramFormDefaultValues;

  const methods = useForm({
    mode: 'all',
    resolver: zodResolver(loyaltyProgramSchema),
    defaultValues: mappedDefaults,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, errors },
    reset,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'goals',
  });

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        filter: {
          and: [
            {
              field: 'status',
              value: [1],
            },
          ],
        },
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  useEffect(() => {
    if (isEdit && defaultValues) {
      reset(loyaltyProgramDefaultValuesMapper(defaultValues));
    }
  }, [isEdit, defaultValues, reset]);

  const handleAddGoal = () => {
    append(generateGoalValues(fields.length + 1));
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} method="POST">
        <div className={styles.formdata}>
          <div
            className={cn(styles.formdataGroup, styles.formdataGroupSettings)}
          >
            <TitleSecondary>Nustatymai</TitleSecondary>
            <div className={styles.formdataGroupContent}>
              <Controller
                name="name"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name="name"
                    label="Pavadinimas"
                    placeholder="Įvesti"
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    disabled={loading}
                    helperText={error?.message}
                  />
                )}
              />
              <div className={styles.inputsRow}>
                <Controller
                  name="valid_from"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name="valid_from"
                      label="Galioja nuo"
                      type="date"
                      max="2999-12-31"
                      placeholder={dateFormat}
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      disabled={loading}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name="valid_to"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name="valid_to"
                      label="Galioja iki"
                      type="date"
                      max="2999-12-31"
                      placeholder={dateFormat}
                      value={value}
                      onChange={e => onChange(e.target.value)}
                      disabled={loading}
                      helperText={error?.message}
                    />
                  )}
                />
              </div>
              <div className={styles.field} style={{ width: '100%' }}>
                <Controller
                  name="period"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div>
                      <Select
                        name="period"
                        label="Tikslus išpildyti per"
                        options={periodOptions}
                        selectedValue={value}
                        onChange={event => onChange(Number(event.target.value))}
                        labelContainerClassName={
                          styles.selectLabelContainerClassName
                        }
                      />
                      {error && (
                        <span className={styles.errorMessage}>
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          <div className={cn(styles.formdataGroup)}>
            <TitleSecondary>Žingsniai</TitleSecondary>
            <div className={styles.goalGroupContainer}>
              {fields.map((field, index) => (
                <React.Fragment key={field.id}>
                  {index > 0 && <Divider className={styles.goalBlockDivider} />}
                  <GoalsGroupForm
                    index={index}
                    control={control}
                    onRemove={() => remove(index)}
                    disableDelete={fields.length <= 1}
                    memberships={memberships}
                    loading={loading}
                  />
                </React.Fragment>
              ))}
              <Button
                color="dark"
                type="button"
                classes={{ button: styles.addGroupButton }}
                onClick={handleAddGoal}
              >
                Pridėti tikslą
              </Button>
            </div>
            {errors?.goals?.message && (
              <span className={styles.errorMessage}>
                {errors.goals.message}
              </span>
            )}
          </div>

          <div className={cn(styles.formdataGroup)}>
            <TitleSecondary>Taisyklės</TitleSecondary>
            <div className={styles.formdataGroupContent}>
              <Controller
                name="description_lt"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name="description_lt"
                    label="🇱🇹 Lietuvių kalba"
                    type="textarea"
                    placeholder="Aprašymas"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    helperText={error?.message}
                  />
                )}
              />
              <Controller
                name="description_en"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name="description_en"
                    label="🇬🇧 Anglų kalba"
                    type="textarea"
                    placeholder="Aprašymas"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    helperText={error?.message}
                  />
                )}
              />
              <Controller
                name="description_ru"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name="description_ru"
                    label="🇷🇺 Rusų kalba"
                    type="textarea"
                    placeholder="Aprašymas"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    helperText={error?.message}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <SubmitActions
          cancel="Atgal"
          cancelButtonColor="outline"
          confirm={isEdit ? 'Išsaugoti pakeitimus' : 'Sukurti'}
          back={config.APP_PATHS.loyaltyProgram}
          loading={loading}
          disabled={isEdit ? !isDirty || !isValid : !isValid}
        />
      </form>
    </FormProvider>
  );
};

const mapStateToProps = state => ({
  loyaltyProgram: state.loyaltyProgram,
  memberships: state.memberships,
});

Form.propTypes = {
  dispatch: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  loyaltyProgram: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(Form);
